import type { PersistedClient } from '@tanstack/react-query-persist-client';
import { hashKey } from '@tanstack/react-query';

import type { UploadPictureDto } from '../../apis/zoning-api';
import {
  MissingCropPictureDrop,
  notifyMissingCropPictures,
} from './missing-crop-picture-error';
import { loadPendingCropPictureFile } from './pending-crop-pictures';

const UPLOAD_CROP_PICTURE_KEY_HASH = hashKey(['uploadCropPicture']);

function isUploadCropPictureMutation(mutationKey: unknown): boolean {
  return (
    Array.isArray(mutationKey) &&
    hashKey(mutationKey as unknown[]) === UPLOAD_CROP_PICTURE_KEY_HASH
  );
}

/**
 * Files cannot be relied on inside the main TanStack IDB snapshot after kill.
 * Keep only `pictureId` — bytes live in `pendingCropPictures`.
 */
export function stripCropPictureFilesFromPersistedClient(
  client: PersistedClient
): PersistedClient {
  const mutations = client.clientState.mutations.map((mutation) => {
    if (!isUploadCropPictureMutation(mutation.mutationKey)) {
      return mutation;
    }
    const variables = mutation.state.variables as UploadPictureDto | undefined;
    if (!variables || variables.picture == null) {
      return mutation;
    }
    const { picture: _picture, ...rest } = variables;
    return {
      ...mutation,
      state: {
        ...mutation.state,
        variables: rest,
      },
    };
  });

  return {
    ...client,
    clientState: {
      ...client.clientState,
      mutations,
    },
  };
}

/**
 * Re-attach File objects from durable IDB before mutations resume.
 * Unrecoverable uploads (no File and no blob for pictureId) are dropped and
 * surfaced via {@link MISSING_CROP_PICTURES_EVENT} so the UI can ask for a retake.
 */
export async function rehydrateCropPictureFilesInPersistedClient(
  client: PersistedClient
): Promise<PersistedClient> {
  const dropped: MissingCropPictureDrop[] = [];
  const mutations: PersistedClient['clientState']['mutations'] = [];

  for (const mutation of client.clientState.mutations) {
    if (!isUploadCropPictureMutation(mutation.mutationKey)) {
      mutations.push(mutation);
      continue;
    }

    const variables = mutation.state.variables as UploadPictureDto | undefined;
    if (!variables) {
      dropped.push({});
      continue;
    }

    if (variables.picture && variables.picture.size > 0) {
      mutations.push(mutation);
      continue;
    }

    if (variables.pictureId) {
      const picture = await loadPendingCropPictureFile(variables.pictureId);
      if (picture && picture.size > 0) {
        mutations.push({
          ...mutation,
          state: {
            ...mutation.state,
            variables: {
              ...variables,
              picture,
            },
          },
        });
        continue;
      }
    }

    // Legacy paused upload without pictureId, or blob wiped from IDB.
    dropped.push({
      measureId: variables.measureId,
      field: variables.field,
      pictureId: variables.pictureId,
    });
  }

  notifyMissingCropPictures(dropped);

  return {
    ...client,
    clientState: {
      ...client.clientState,
      mutations,
    },
  };
}
